import type { FC } from 'react';
import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useFormContext } from 'react-hook-form';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import { tasksFormValidation } from '../../utils';
import { useAnchorMenu } from '../../../../hooks';
import { Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, ExpandMore as ExpandMoreOutlined, InfoOutlined } from '@mui/icons-material';
import getActionLevelTooltip from '../../utils/getActionLevelTooltip';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getReferencesOptions } from '../../facebook';
import type { Option } from '../../../../types/shared';

interface IProps {
  indexTask: number;
  indexVariant?: number;
}

const AddGoogleActionButton: FC<IProps> = observer(({ indexTask, indexVariant }) => {
  const { watch } = useFormContext();
  const ruleLevel = watch('level') as string;
  const tasksFormStore = useCurrentTasksFormStore();
  const selectedActions = tasksFormStore.getTask(indexTask)?.actions.map((act) => act.action);

  const { isAddActionAvailable } = tasksFormValidation({ tasksForm: tasksFormStore.tasksForm, indexTask });
  const { handleCloseMenu, handleOpenMenu, anchor, isOpen } = useAnchorMenu();

  const { data: actions } = useSuspenseQuery({
    ...getReferencesOptions('google'),
    select: (data) => {
      return data.actions.map(({ name, levels }) => {
        const disabled = !levels.some((lvl) => lvl.id === ruleLevel);
        return {
          value: name,
          label: name,
          disabled,
          tooltip: disabled ? getActionLevelTooltip(levels.map((lvl) => lvl.name).join(' ')) : undefined,
          icon: disabled ? <InfoOutlined color={'inherit'} sx={{ fontSize: 21, p: 0, m: 0 }} /> : undefined,
        };
      }) as Option[];
    },
  });

  const filteredActions = useMemo(() => {
    return actions.filter((item) => !selectedActions?.includes(String(item.value)));
    // selectedActionIds as useEffect do not track mobx proxy object changes
  }, [actions, selectedActions]);

  const handleAddAction = (action: string) => {
    tasksFormStore.addAction({ indexTask, indexVariant, action });
    handleCloseMenu();
  };

  return (
    <>
      <Button
        size="medium"
        startIcon={<AddIcon />}
        variant="text"
        onClick={handleOpenMenu}
        disabled={!isAddActionAvailable}
        endIcon={
          <ExpandMoreOutlined
            sx={(t) => ({
              fontSize: 14,
              color: isAddActionAvailable ? t.palette.primary.main : t.palette.grey[300],
              transform: `rotate(${isOpen ? '180deg' : '0deg'})`,
              transition: 'transform .1s',
            })}
          />
        }
      >
        Add action
      </Button>
      <Menu anchorEl={anchor} open={isOpen} onClose={handleCloseMenu} sx={{ maxHeight: '470px' }}>
        {filteredActions.map((item) => (
          <Tooltip
            key={`Task:${indexTask}.ActionType:${item.value}:${item.label}`}
            title={item.tooltip}
            placement={'right'}
            arrow
          >
            <MenuItem
              disableRipple={item.disabled}
              disableTouchRipple={item.disabled}
              sx={(t) => ({
                display: 'flex',
                justifyContent: item.disabled ? 'space-between' : 'start',
                alignItems: 'center',
                color: item.disabled ? t.palette.text.disabled : t.palette.grey[900],
                cursor: item.disabled ? 'default' : 'pointer',
                '&.MuiMenuItem-root:hover': item.disabled ? { backgroundColor: 'transparent' } : {},
              })}
              onClick={() => {
                if (item.disabled) return;
                handleAddAction(String(item.value));
              }}
            >
              <Typography variant={'body2'} sx={{ p: 0, m: 0 }}>
                {item.label}
              </Typography>
              {item.disabled && <InfoOutlined color={'inherit'} />}
            </MenuItem>
          </Tooltip>
        ))}
      </Menu>
    </>
  );
});

export default AddGoogleActionButton;
